import { createRouter, createWebHashHistory } from 'vue-router'
import { useAuthStore } from '@/shared/stores/auth'

const routes = [
  {
    path: '/login',
    component: () => import('@/modules/auth/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/forgot-password',
    component: () => import('@/modules/auth/views/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/reset/:uid/:token',
    component: () => import('@/modules/auth/views/ResetPasswordView.vue'),
    meta: { public: true },
  },
  { path: '/', component: () => import('@/modules/dashboard/views/DashboardView.vue') },
  { path: '/queue', component: () => import('@/modules/queue/views/QueueRouter.vue') },
  // Lignes (trunks sortants) de l'agent : choix de la ligne active. Accessible
  // à tous ; la vue gère les cas 0/1 ligne. La nav la masque si ≤ 1 ligne.
  { path: '/lines', component: () => import('@/modules/lines/views/LinesView.vue') },
  // Répertoire interne (postes + appel interne) — accessible à tous les rôles.
  { path: '/repertoire', component: () => import('@/modules/directory/views/RepertoireView.vue') },
  {
    // Page dédiée d'une campagne côté AGENT : démarrage de la session
    // d'appels automatique + numéros en main pour cette campagne.
    path: '/queue/campaign/:id',
    component: () => import('@/modules/queue/views/AgentCampaignView.vue'),
  },
  {
    path: '/campaigns',
    component: () => import('@/modules/campaigns/views/CampaignsView.vue'),
    // Campagnes = gestion ; team_admin et au-dessus uniquement
    meta: { requiresTeamAdmin: true },
  },
  {
    path: '/campaigns/new',
    component: () => import('@/modules/campaigns/views/CampaignEditView.vue'),
    meta: { requiresTeamAdmin: true },
  },
  {
    path: '/campaigns/:id/edit',
    component: () => import('@/modules/campaigns/views/CampaignEditView.vue'),
    meta: { requiresTeamAdmin: true },
  },
  { path: '/callbacks', component: () => import('@/modules/queue/views/CallbacksView.vue') },
  {
    // Mon planning : chaque utilisateur voit son planning assigné (rappels
    // 5 min avant chaque bloc). Les onglets de GESTION (éditer/assigner/
    // valider les heures) ne s'affichent que pour owner/admins autorisés —
    // et le serveur les refuse de toute façon aux autres.
    path: '/planning',
    component: () => import('@/modules/planning/views/PlanningView.vue'),
  },
  {
    // Vue contacts UNIFIÉE : tous les numéros enrichis (carnet + liste noire +
    // statut client), recherche/filtres, actions. Admin.
    path: '/contacts-hub',
    component: () => import('@/modules/contacts/views/ContactsHubView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/recordings',
    component: () => import('@/modules/recordings/views/RecordingsView.vue'),
  },
  {
    path: '/supervisor',
    component: () => import('@/modules/supervisor/views/Dashboard.vue'),
    meta: { requiresTeamAdmin: true },
  },
  {
    path: '/users',
    component: () => import('@/modules/users/views/UsersView.vue'),
    meta: { requiresCanManageUsers: true },
  },
  {
    path: '/telephony',
    component: () => import('@/modules/telephony/views/TelephonyView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/cdr',
    component: () => import('@/modules/cdr/views/CDRView.vue'),
  },
  {
    path: '/agent-time',
    component: () => import('@/modules/supervisor/views/AgentTimeView.vue'),
    // Temps agents = supervision ; team_admin (sa propre équipe) et au-dessus.
    meta: { requiresTeamAdmin: true },
  },
  {
    path: '/statistics',
    component: () => import('@/modules/statistics/views/StatisticsView.vue'),
    // Statistiques (dispositions, qualité, fiche qualifiée) — superviseur+.
    meta: { requiresTeamAdmin: true },
  },
  {
    // Liste qualifiée : appels répondus + toutes les infos de contact,
    // export CSV/Excel configurable. Owner toujours ; admin seulement si le
    // owner lui a accordé l'accès (droit résolu côté serveur).
    path: '/qualified-list',
    component: () => import('@/modules/qualified-list/views/QualifiedListView.vue'),
    meta: { requiresQualifiedList: true },
  },
  {
    path: '/performance',
    component: () => import('@/modules/performance/views/PerformanceView.vue'),
    // Page Performance (graphes, score Vente/RDV, qualité des listes) — superviseur+.
    meta: { requiresTeamAdmin: true },
  },
  {
    // Détail du temps d'UN agent : filtres précis (plage libre), totaux par
    // statut, comparatif Disponible/En appel, journal groupé par jour.
    path: '/agent-time/:id',
    component: () => import('@/modules/supervisor/views/AgentTimeDetailView.vue'),
    meta: { requiresTeamAdmin: true },
  },
  {
    path: '/forms',
    component: () => import('@/modules/forms/views/FormsView.vue'),
    meta: { requiresTeamAdmin: true },
  },
  {
    path: '/scripts',
    component: () => import('@/modules/scripts/views/ScriptsView.vue'),
    // Accessible aux agents (gestion de leurs scripts personnels) ; la vue
    // s'adapte au rôle.
  },
  {
    path: '/appointments',
    component: () => import('@/modules/appointments/views/AppointmentsView.vue'),
    // Accessible aux agents : ils consultent leurs PROPRES rendez-vous. La vue
    // et le backend filtrent par rôle (un agent ne voit que les siens ; stats,
    // filtres agent/équipe et colonne Agent réservés aux superviseurs+).
  },
  {
    // Ventes — même logique de portée par rôle que les rendez-vous.
    path: '/sales',
    component: () => import('@/modules/sales/views/SalesView.vue'),
  },
  {
    // Suivi d'appels Recouvrement / Fidélisation : revue + validation des
    // appels qualifiés avec ces statuts. Réservé admin/owner (pilotage).
    path: '/follow-up',
    component: () => import('@/modules/follow-up/views/FollowUpView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/ring-groups',
    component: () => import('@/modules/telephony/views/RingGroupsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/blacklist',
    component: () => import('@/modules/telephony/views/BlacklistView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/number-lists',
    component: () => import('@/modules/queue/views/NumberListsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/call-flows',
    component: () => import('@/modules/call-flow/views/CallFlowsView.vue'),
    meta: { requiresAdmin: true },
  },
  {
    path: '/settings',
    component: () => import('@/modules/settings/views/SettingsView.vue'),
  },
  {
    // Messagerie interne : direct, groupes, canal d'équipe — tous rôles.
    path: '/chat',
    component: () => import('@/modules/chat/views/ChatView.vue'),
  },
  // Centre d'aide intégré — accessible à tous ; le contenu est filtré PAR RÔLE
  // côté composant (un agent ne voit pas les rubriques d'administration).
  { path: '/aide', component: () => import('@/modules/docs/views/DocsHomeView.vue') },
  // Retours sur l'aide — réservé au compte propriétaire (root). DÉFINI AVANT
  // `/aide/:sectionId` pour que « retours » ne soit pas pris pour une rubrique.
  {
    path: '/aide/retours',
    component: () => import('@/modules/docs/views/DocsFeedbackView.vue'),
    meta: { requiresOwner: true },
  },
  { path: '/aide/:sectionId', component: () => import('@/modules/docs/views/DocsSectionView.vue') },
  { path: '/aide/:sectionId/:articleId', component: () => import('@/modules/docs/views/DocsSectionView.vue') },
]

const router = createRouter({ history: createWebHashHistory(), routes })

router.beforeEach(async (to, _from, next) => {
  const auth = useAuthStore()
  if (!auth.initialized) await auth.loadProfile()

  if (!to.meta.public && !auth.isAuthenticated) return next('/login')
  if (to.path === '/login' && auth.isAuthenticated) return next('/')

  // Legacy adminOnly support
  if (to.meta.adminOnly && !auth.isStaff) return next('/')

  // Role-based guards
  if (to.meta.requiresTeamAdmin && !auth.isTeamAdmin) return next('/')
  if (to.meta.requiresAdmin && !auth.isAdmin) return next('/')
  if (to.meta.requiresOwner && !auth.isOwner) return next('/')
  if (to.meta.requiresQualifiedList && !auth.canSeeQualifiedList) return next('/')
  if (to.meta.requiresCanManageUsers && !auth.canManageUsers) return next('/')

  next()
})

export default router
